import { ClerkProvider } from '@clerk/clerk-react'
import { router } from '../routes'
import { captureReferralCode } from '../features/affiliate/capture-referral'
import { captureAttribution } from '../lib/capture-attribution'

// Capture ?plan= from URL on first load — persists through Clerk auth redirects
const urlPlan = new URLSearchParams(window.location.search).get('plan')
// 2026-06-07: Agency removed from customer-facing signup. Only starter/pro
// URLs are accepted; an 'agency' query param is now silently ignored.
if (urlPlan && ['starter', 'pro'].includes(urlPlan)) {
  localStorage.setItem('earlyAccessPlan', urlPlan)
}

// Capture ?promo=CODE the same way (marketing sends signup?promo=CLOSE20).
// /billing forwards it to POST /api/billing/checkout, which pre-applies the
// matching Stripe promotion code. Unknown codes are ignored server-side.
const urlPromo = new URLSearchParams(window.location.search).get('promo')
if (urlPromo) {
  const code = urlPromo.trim().toUpperCase()
  if (/^[A-Z0-9_-]{2,32}$/.test(code)) {
    try {
      localStorage.setItem('dca_promo', code)
    } catch {
      // localStorage disabled — promo is best-effort
    }
  }
}

// Capture ?ref=CODE before Clerk hijacks the URL during the signup redirect
// dance. The code is stashed in localStorage and attributed after auth in
// the protected-route layer (features/affiliate/capture-referral.ts).
captureReferralCode()

// Capture UTM params / referrer the same way (first touch wins), stamped on
// the user post-auth via POST /auth/attribution (lib/capture-attribution.ts).
captureAttribution()

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!CLERK_PUBLISHABLE_KEY) {
  console.warn('Missing VITE_CLERK_PUBLISHABLE_KEY — auth will not work')
}

export function AuthProvider({ children }: { readonly children: React.ReactNode }) {
  if (!CLERK_PUBLISHABLE_KEY) {
    return <>{children}</>
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      signInUrl="/login"
      signUpUrl="/signup"
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/dashboard"
      routerPush={(to) => router.navigate(to)}
      routerReplace={(to) => router.navigate(to, { replace: true })}
    >
      {children}
    </ClerkProvider>
  )
}
